import type { BattleActivateEffect } from 'features/battle/types';
import type { AssociatedLink } from 'features/catalog/badge-associated-links';
import type { RarityName } from 'features/catalog/rarity-config';

export const BADGE_STAT_KEYS = ['culture', 'engineering', 'growth', 'fun', 'spotlight'] as const;

export type BadgeStatKey = (typeof BADGE_STAT_KEYS)[number];

export const BADGE_STAT_LABELS: Record<BadgeStatKey, string> = {
  culture: 'Culture',
  engineering: 'Engineering',
  growth: 'Growth',
  fun: 'Fun',
  spotlight: 'Spotlight',
};

export const isBadgeStatKey = (value: string): value is BadgeStatKey => BADGE_STAT_KEYS.some((key) => key === value);

export type BadgeStatScore = 0 | 1 | 2 | 3 | 4 | 5;
export type BadgeStats = Record<BadgeStatKey, BadgeStatScore>;

export const ZERO_BADGE_STATS: BadgeStats = {
  culture: 0,
  engineering: 0,
  growth: 0,
  fun: 0,
  spotlight: 0,
};

/** Sparse catalog helper — omitted axes are 0. */
export const badgeStats = (partial: Partial<BadgeStats>): BadgeStats => {
  const stats = { ...ZERO_BADGE_STATS, ...partial };
  for (const key of BADGE_STAT_KEYS) {
    const n = stats[key];
    if (!Number.isInteger(n) || n < 0 || n > 5) {
      throw new Error(`badgeStats: ${key} must be an integer 0–5, got ${String(n)}`);
    }
  }
  return stats;
};

export interface BadgeDescription {
  name: string;
  description: string;
  rarity: RarityName;
  /** Five character-sheet axes (0–5). Omitted keys are 0 via `badgeStats`. */
  stats: BadgeStats;
  /** Battle effect (`trigger`: Activate, Ongoing, or On Reveal). Omitted common/rare badges are towers (equal pair, common 4+2, or a common 5). */
  effect?: BattleActivateEffect;
  credits: string;
  design?: string;
  associatedLinks?: AssociatedLink[];
  // If any of these ids are missing, the badge cannot be claimed yet.
  // You need to have at least count of ids to claim this badge.
  restrictedRules?: { count: number; ids: string[] };
  // If any of these ids are present, this badge gets automatically claimed.
  // You automatically claim this badge if you have at least count of ids.
  autoClaimRules?: { count: number; ids: string[] };
  // When true, the badge cannot be self-claimed in edit mode.
  // Pair with autoClaimRules only when unmet deps should also force the badge off (e.g. Explorer).
  banSelfClaim?: boolean;
  /** Custom hover lock line for `banSelfClaim` (shown as `🔒 …`).
   *  With multi-count `autoClaimRules`, treated as the progress unit (e.g. `2/5 Offices`). */
  banSelfClaimHint?: string;
  // When true, this badge always sorts near the end of its group (even when claimed).
  // `banSelfClaim` badges sort even later automatically — no need to set both.
  forceSortLast?: boolean;
  // When true, never surface this badge in "Claimed by others" suggestions.
  suppressSuggest?: boolean;
  /** Catalog drop time — `YYYY-MM-DD` (local noon) or an ISO timestamp for a precise instant.
   *  Date-only values appear from that calendar day; timed values wait until that instant. */
  dateAdded: string;
  /**
   * Offsite event year (event assumed November 1). When set, `restrictedRules` are ignored and
   * tenure gates are computed at runtime so the unlock keeps matching “employed by the offsite.”
   */
  offsiteYear?: number;
  /**
   * Event calendar day (`YYYY-MM-DD`). Same years-since tenure ladder as offsits, anchored
   * to this date instead of November 1. When set, `restrictedRules` are ignored.
   */
  tenureGateEventDate?: string;
  /**
   * Thematic neighbors (namesakes, inverses, same activity) — not unlock gates.
   * Filled from `RELATED_BADGE_CLUSTERS` after the catalog map is built; listing here is optional.
   */
  relatedIds?: string[];
}
